import {
  ClaimContext,
  ClaimType,
  DamageDescription,
  MissingItemDescription,
  OrderItem,
} from '../../parcel-finder/claims/types';
import { UploaderFile } from '../../common/Uploader';

export function buildSubmitClaimDTO(context: ClaimContext) {
  const { type } = context;

  switch (type) {
    case ClaimType.INVESTIGATION:
      return buildInvestigationClaimDTO(context);

    case ClaimType.WHOLE_DAMAGE:
      return buildWholeDamageDTO(context);

    case ClaimType.PARTIAL_DAMAGE:
      return buildPartialDamageDTO(context);

    case ClaimType.MISSING_ITEM:
      return prepareMissingItemClaimData(context);

    case ClaimType.CASH_ON_DELIVERY:
      return buildCashOnDeliveryClaimDTO(context);

    default:
      throw new Error(`Doesn't support claim type "${type}"`);
  }
}

function buildCommonClaimDTO(claim: ClaimContext) {
  const {
    draftId,
    type,
    shipmentId,
    isReturnShipment,
    trackingCode,
    recipientName,
    weight,
    netValue,
    netValueCurrency,
    trackingScreenshot,
    invoiceDocuments,
    deliveryDate,
    deliveredToMailboxDate,
    postalReturnDeliveryDate,
    recipientAddress,
    recipientCity,
    recipientZipCode,
    recipientCountryCode,
    recipientEmail,
    recipientPhoneNumber,
    ssHubScanDate,
    firstHubScanDate,
    postalReturnDate,
    shipmentCreationDate,
    carrierCode,
    carrierCountryCode,
    customerComment,
    shopOrderId,
    outsideOfDeadlineReason,
    deadlineSupportingDocuments,
    shopClaimId,
    otherDocuments,
    isMultiShipmentOrder,
    isTaxIncluded,
    taxRate,
    isOrderItemsRequired,
    isOrderItemTaxRate,
    dataProcessingConsent,
  } = claim;

  return {
    draftId,
    claimType: type,
    shipmentId,
    isReturnShipment,
    trackingCode,
    carrierCode,
    carrierCountryCode,
    firstHubScanDate,
    ssHubScanDate,
    recipientName,
    weight: parseNumber(weight),
    deliveryDate,
    deliveredToMailboxDate: deliveredToMailboxDate || undefined,
    postalReturnDate: postalReturnDate || undefined,
    postalReturnDeliveryDate: postalReturnDeliveryDate || undefined,
    shipmentCreationDate: shipmentCreationDate || undefined,
    netValue: parseMoney(netValue),
    netValueCurrency,
    trackingScreenshot: getFileId(trackingScreenshot),
    invoiceDocuments: getFileList(invoiceDocuments),
    customerComment,
    recipientAddress,
    recipientCity,
    recipientZipCode,
    recipientCountryCode,
    recipientEmail,
    recipientPhoneNumber,
    shopOrderId,
    outsideOfDeadlineReason,
    shopClaimId,
    deadlineSupportingDocuments: getFileList(deadlineSupportingDocuments),
    orderItems: buildOrderItemsDTO(claim),
    otherDocuments: getFileList(otherDocuments),
    isMultiShipmentOrder,
    isTaxIncluded: isOrderItemsRequired ? isTaxIncluded : false,
    taxRate: taxRate ? parsePercentage(taxRate) : null,
    isOrderItemTaxRate: isOrderItemsRequired ? isOrderItemTaxRate : false,
    dataProcessingConsent: getFileId(dataProcessingConsent),
  };
}

export function buildInvestigationClaimDTO(claim: ClaimContext) {
  return {
    ...buildCommonClaimDTO(claim),
    declarationOfRecipient: getFileId(claim.declarationOfRecipient),
  };
}

function buildDamageOrMissingItemCommonClaimDTO(claim: ClaimContext) {
  const carrierDamageReport = getFileList(claim.carrierDamageReport);
  return {
    ...buildCommonClaimDTO(claim),
    warehouseDeliveryDate: claim.warehouseDeliveryDate,
    lmcCaseId: claim.lmcCaseId,
    ...(carrierDamageReport?.length && { carrierDamageReport }),
  };
}

export function buildWholeDamageDTO(claim: ClaimContext) {
  return {
    ...buildDamageOrMissingItemCommonClaimDTO(claim),
    picturesOfDamage: getFileList(claim.picturesOfDamage),
    damageDescription: claim.damageInfo || claim.wholeDamageInfo,
  };
}

export function buildPartialDamageDTO(claim: ClaimContext) {
  return {
    ...buildDamageOrMissingItemCommonClaimDTO(claim),
    picturesOfDamage: getFileList(claim.picturesOfDamage),
    damageDescription: claim.damageInfo || serializeItemDescription(claim.partialDamageInfo),
  };
}

export function prepareMissingItemClaimData(claim: ClaimContext) {
  return {
    ...buildDamageOrMissingItemCommonClaimDTO(claim),
    picturesOfMissingItem: getFileList(claim.picturesOfMissingItem),
    missingItemDescription: serializeOrderItems(claim.orderItems) || serializeItemDescription(claim.missingItemInfo),
  };
}

export function buildCashOnDeliveryClaimDTO(claim: ClaimContext) {
  return {
    ...buildCommonClaimDTO(claim),
    cashOnDeliveryPaymentType: claim.cashOnDeliveryPaymentType,
    proofOfConsumerPayment: getFileId(claim.proofOfConsumerPayment),
  };
}

function buildOrderItemsDTO(claim: ClaimContext) {
  const { isOrderItemsRequired, orderItems, isOrderItemTaxRate } = claim;

  if (!isOrderItemsRequired) {
    return [];
  }

  return orderItems.map((item) => ({
    id: item.orderItemId,
    unitPrice: parseMoney(item.unitPrice),
    quantity: parseNumber(item.quantity),
    description: item.description,
    taxRate: isOrderItemTaxRate && item.taxRate ? parsePercentage(item.taxRate) : null,
  }));
}

function getFileId(files: UploaderFile[] | undefined) {
  return files?.[0]?.response?.body?.id;
}

function getFileList(files: UploaderFile[] | undefined) {
  return files?.map?.((x) => x.response?.body?.id);
}

function serializeOrderItems(orderItems: OrderItem[]) {
  if (!orderItems || orderItems.length === 0) {
    return '';
  }

  return orderItems
    .map((item) => {
      const { description, quantity } = item;
      return `${quantity}x ${description}`;
    })
    .join('; ');
}

function isDamageDescription(desc: DamageDescription | MissingItemDescription): desc is DamageDescription {
  return !!(desc as DamageDescription).damageDescription;
}

function serializeItemDescription(desc: Array<DamageDescription | MissingItemDescription>) {
  const strings = desc.map(
    (item) => `${item.amount}x ${item.itemName}${isDamageDescription(item) ? `, ${item.damageDescription}` : ''}`
  );
  return strings.join('; ');
}

function parseNumber(str: string | number | undefined | null): number | null {
  if (str === undefined || str === null) {
    return null;
  }

  const parsed = parseFloat(str.toString());
  return isNaN(parsed) ? null : parsed;
}

function parseMoney(str: string | number | undefined | null): number | null {
  if (str === undefined || str === null) {
    return null;
  }

  const parsed = parseFloat(str.toString());
  return isNaN(parsed) ? null : Math.floor(parsed * 100);
}

function parsePercentage(str: string | number | undefined | null): number | null {
  if (str === undefined || str === null) {
    return null;
  }

  const parsed = parseFloat(str.toString());
  return isNaN(parsed) ? null : parsed / 100;
}
